import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';

export interface AzTableColumn {
  key: string; // Property name in data object
  label: string; // Display label in header
  width?: string; // Optional width (e.g., '200px', '20%', 'auto')
  minWidth?: string; // Optional min-width (e.g., '100px')
  maxWidth?: string; // Optional max-width (e.g., '300px')
  align?: 'left' | 'center' | 'right'; // Text alignment
}

export interface AzTableAction<T = unknown> {
  icon: string; // Material icon name
  label: string; // Tooltip/aria-label
  callback: (row: T) => void; // Action callback
}

@Component({
  selector: 'app-az-table',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './az-table.component.html',
  styleUrl: './az-table.component.scss',
})
export class AzTableComponent<T = unknown> {
  @Input() data: T[] = [];
  @Input() columns: AzTableColumn[] = [];
  @Input() actions: AzTableAction<T>[] = [];
  @Input() emptyMessage = 'No data available';

  @Output() rowClick = new EventEmitter<T>();

  onRowClick(row: T) {
    this.rowClick.emit(row);
  }

  onActionClick(action: AzTableAction<T>, row: T, event: Event) {
    event.stopPropagation();
    action.callback(row);
  }

  getCellValue(row: T, column: AzTableColumn): string {
    const value = (row as Record<string, unknown>)[column.key];
    if (value == null) return '--';
    return String(value);
  }

  getColumnWidth(column: AzTableColumn): string | null {
    // For auto width, return null to let table layout handle it
    if (column.width === 'auto') return null;
    return column.width || null;
  }

  getColumnMinWidth(column: AzTableColumn): string | null {
    return column.minWidth || null;
  }

  getColumnMaxWidth(column: AzTableColumn): string | null {
    return column.maxWidth || null;
  }

  getColumnAlign(column: AzTableColumn): string {
    return column.align || 'left';
  }
}
